/*
  assets/artisan-onglets.js
  Tab switching for sections/artisan-collections-onglets.liquid.

  The panels are already in the DOM and the first one is open before this
  script runs, so a failure here leaves a working page rather than a blank
  one. All this adds is the ability to swap which panel is showing.

  The tablist lives outside the element (next to the heading), so it is
  located by aria-controls rather than by descent.
*/

class ArtisanOnglets extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (this.panels.length < 2) return;

    this.tabs = this.panels
      .map((panel) => document.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`))
      .filter(Boolean);
    if (this.tabs.length !== this.panels.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      // Only the active tab stays reachable by Tab; the arrows move within.
      tab.setAttribute('tabindex', active ? '0' : '-1');
      this.panels[i].toggleAttribute('hidden', !active);
    });

    // The heading changes with the tab: every candidate title is already in
    // the h2 next to the tablist, one per panel, and only the one that names
    // the open panel is shown.
    const root = this.closest('.tab') || document;
    const open = this.panels[index].id;
    root.querySelectorAll('[data-tab-title-for]').forEach((title) => {
      title.toggleAttribute('hidden', title.dataset.tabTitleFor !== open);
    });
  }
}

if (!customElements.get('artisan-onglets')) {
  customElements.define('artisan-onglets', ArtisanOnglets);
}
